/**
 * GeneralMapView Component (Web)
 *
 * A React web map view using Google Maps API with clustering, markers, and custom callouts.
 * Includes Floating Action Buttons (FABs) for map reset and navigation.
 *
 * Props:
 * - `data`: Array of marker data.
 * - `initialCenter`: Initial center coordinates for the map.
 * - `onNavToListView`: Callback for navigation to list view.
 * - `onNavToDetail`: Callback for navigation to detail view.
 * - `markerIcons`: Custom icons for active/inactive markers.
 * - `customCalloutProps`: Props for customizing callouts.
 *
 * Dependencies:
 * - @react-google-maps/api
 * - FontAwesome icons
 */
import React, { useRef, useState, useEffect } from "react";
import { GoogleMap, Marker, MarkerClusterer, InfoWindow } from "@react-google-maps/api";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faList, faSatellite } from "@fortawesome/free-solid-svg-icons";
import CustomFab from "../../../components/CustomFAB/CustomFAB";
import CustomCallout from "../../../components/CustomCallout/CustomCallout.web";
import GoogleMapsLoader from "../../../components/GoogleMapsLoader/GoogleMapsLoader.web";
import ClusterMarker from "../../../assets/images/cluster-marker.png";
import { COLORS } from "../../../constants";
import { sanitizeCoordinate } from "../../../utils/formatting";
import "./MapStyles.css";
import { useBottomTabBarHeight } from "@react-navigation/bottom-tabs";
import AppSettings from "../../../storage/appSettings";
import ParkingStore from "../../../storage/parkingStore";

const GeneralMapView = ({ data, initialCenter, onNavToListView, onNavToDetailView, markerIcons, calloutProps }) => {
  const toggleSatelliteView = AppSettings((state) => state.toggleSatelliteView);
  const satteliteView = AppSettings((state) => state.satelliteView);
  const showMeter = ParkingStore((state) => state.showMeter);
  const showStructure = ParkingStore((state) => state.showStructure);
  const parkingData = ParkingStore((state) => state.parking);
  const bottomTabBarHeight = useBottomTabBarHeight();
  const mapRef = useRef(null);

  const [activeMarker, setActiveMarker] = useState(null);

  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    fullscreenControl: false,
    controlSize: 32,
    mapTypeControl: false,
    clickableIcons: false,
    streetViewControl: false,
  };
  const clusterStyles = [
    {
      url: ClusterMarker,
      height: 40,
      width: 40,
      textColor: COLORS.textLight,
      textSize: 12,
    },
  ];

  useEffect(() => {
    const selectedParkingType = parkingData.find((parking) => parking.id === activeMarker)?.type;
    if (activeMarker && !showMeter && !selectedParkingType) {
      setActiveMarker(null);
    } else if (activeMarker && !showStructure && !selectedParkingType) {
      setActiveMarker(null);
    }
  }, [showMeter, showStructure]);

  /**
   * Resets the map view to its initial center and zoom level.
   */
  const handleResetMap = () => {
    if (mapRef.current) {
      mapRef.current.setCenter(initialCenter);
      mapRef.current.setZoom(12);
    }
    setActiveMarker(null);
  };

  /**
   * Handles marker click events.
   * Sets the active marker or deselects it if already selected.
   *
   * @param {string} markerId - The ID of the clicked marker.
   */
  const handleMarkerClick = (markerId) => {
    if (markerId !== activeMarker) {
      setActiveMarker(markerId);
    } else {
      setActiveMarker(null);
    }
  };
  /**
   * Generate a unique ID for locations without an ID.
   * @param {object} location - The location object.
   * @param {number} index - The index of the location in the array.
   * @returns {string} - A unique ID for the location.
   */
  const generateMarkerId = (location, index) => {
    return location.id || `${location.name}-${location.latitude}-${index}`;
  };

  return (
    <GoogleMapsLoader>
    <div
      style={{
        ...styles.container,
        height: `calc(100vh - ${bottomTabBarHeight}px)`,
      }}
    >
      <div style={styles.mapWrapper}>
        <GoogleMap
          mapContainerStyle={styles.mapContainer}
          defaultCenter={initialCenter}
          zoom={12}
          onLoad={(map) => {
            mapRef.current = map;
            handleResetMap();
          }}
          options={{
            mapTypeId: satteliteView ? "satellite" : "roadmap",
            disableDefaultUI: false,
            zoomControl: true,
            fullscreenControl: false,
            controlSize: 32,
            mapTypeControl: true,
            ...mapOptions,
          }}
          mapContainerClassName="custom-map-controls"
        >
          <MarkerClusterer options={{ styles: clusterStyles }}>
            {(clusterer) =>
              data.map((location, index) => {
                // Get the latitude and longitude from the location object.  Bathroom locations have a geometry object with x and y properties, while other locations have latitude and longitude properties.
                const latitude = location.geometry
                  ? sanitizeCoordinate(location.geometry.y.N)
                  : sanitizeCoordinate(location.latitude);
                const longitude = location.geometry
                  ? sanitizeCoordinate(location.geometry.x.N)
                  : sanitizeCoordinate(location.longitude);
                const markerId = generateMarkerId(location, index);
                const icons = typeof markerIcons === "function" ? markerIcons(location) : markerIcons;
                return (
                  <Marker
                    key={markerId}
                    data-testid={`map-marker-${markerId}`}
                    title={calloutProps.title(location)}
                    position={{
                      lat: latitude,
                      lng: longitude,
                    }}
                    clusterer={clusterer}
                    icon={activeMarker === markerId ? icons.enabled : icons.disabled}
                    onClick={() => handleMarkerClick(markerId)}
                  >
                    {activeMarker === markerId && (
                      <InfoWindow onCloseClick={() => setActiveMarker(null)}>
                        <CustomCallout
                          title={calloutProps.title(location)}
                          subtitle={calloutProps.subtitle(location)}
                          {...(calloutProps?.chipProps ? { chipProps: calloutProps.chipProps(location) } : {})}
                          onPress={() => {
                            setActiveMarker(null);
                            onNavToDetailView(location);
                          }}
                        />
                      </InfoWindow>
                    )}
                  </Marker>
                );
              })
            }
          </MarkerClusterer>
        </GoogleMap>
        {/* FAB Controls */}
        <div style={styles.fabSatelliteContainer}>
          <div style={styles.fabWrapper}>
            <CustomFab
              onFabClick={toggleSatelliteView}
              icon={faSatellite}
              size="2x"
              color={COLORS.textLight}
              backgroundColor={satteliteView ? COLORS.secondary : COLORS.textDark}
              FontAwesomeIcon={FontAwesomeIcon}
              accessibilityLabel="Satellite"
            />
          </div>
        </div>
        <div style={{ ...styles.fabContainer }}>
          <div style={styles.fabWrapper}>
            <CustomFab
              onFabClick={handleResetMap}
              icon={faRotateRight}
              size="lg"
              color={COLORS.textLight}
              FontAwesomeIcon={FontAwesomeIcon}
              accessibilityLabel="Reset"
            />
          </div>

          <div style={styles.fabWrapper}>
            <CustomFab
              onFabClick={onNavToListView}
              icon={faList}
              size="lg"
              color={COLORS.textLight}
              FontAwesomeIcon={FontAwesomeIcon}
              accessibilityLabel="List View"
            />
          </div>
        </div>
      </div>
    </div>
    </GoogleMapsLoader>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
  },
  mapWrapper: {
    flex: 1,
    width: "100%",
  },
  mapContainer: {
    height: "100%",
    width: "100%",
  },
  fabContainer: {
    position: "absolute",
    bottom: "16px",
    right: "0",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    zIndex: 10,
  },
  fabWrapper: {
    backgroundColor: "transparent",
    borderRadius: 50,
    width: "56px",
    height: "56px",
  },
  fabSatelliteContainer: {
    position: "absolute",
    bottom: "16px",
    left: 32,
    zIndex: 10,
  },
};

export default GeneralMapView;
